import React, { useEffect, useMemo, useState } from 'react';
import './Share.css';

type Platform = 'ios' | 'android' | 'desktop';

const UNIVERSAL_LINK = 'https://www.healmusicapp.com/share';
const IOS_APP_STORE_URL = 'https://apps.apple.com/gb/app/heal-music-breath/id6746941693';
const DESKTOP_URL = 'https://www.healmusicapp.com/';

function detectPlatform(): Platform {
  const ua = navigator.userAgent || '';
  const platform = (navigator as any).userAgentData?.platform || navigator.platform || '';

  const isAndroid = /Android/i.test(ua);
  const isIPhoneIPodIPad = /iPhone|iPod|iPad/i.test(ua);
  const isIPadOS13Plus = platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1;
  const isIOS = isIPhoneIPodIPad || isIPadOS13Plus;

  if (isIOS) return 'ios';
  if (isAndroid) return 'android';
  return 'desktop';
}

function isSameUrlAsHere(target: string) {
  try {
    const here = new URL(window.location.href);
    const t = new URL(target);
    return here.origin === t.origin && here.pathname.replace(/\/+$/, '') === t.pathname.replace(/\/+$/, '');
  } catch {
    return false;
  }
}

const Share: React.FC = () => {
  const platform = useMemo(() => detectPlatform(), []);
  const [status, setStatus] = useState<'trying' | 'fallback' | 'ready'>('trying');

  useEffect(() => {
    if (platform === 'desktop') {
      window.location.replace(DESKTOP_URL);
      return;
    }

    // If we're already on the universal link URL, don't redirect to ourselves.
    // In that case, just present the fallback UI.
    if (isSameUrlAsHere(UNIVERSAL_LINK)) {
      setStatus('ready');
      return;
    }

    setStatus('trying');

    let didHide = false;
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        didHide = true;
      }
    };
    document.addEventListener('visibilitychange', onVis);

    // Attempt to open the app via the Universal Link.
    window.location.href = UNIVERSAL_LINK;

    const timeout = window.setTimeout(() => {
      document.removeEventListener('visibilitychange', onVis);

      // If the page was backgrounded, the app likely opened successfully; do nothing.
      if (didHide || document.visibilityState === 'hidden') return;

      setStatus('fallback');
      if (platform === 'ios') {
        window.location.replace(IOS_APP_STORE_URL);
      }
      // Android fallback stays on-page ("Coming soon").
    }, 1500);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.clearTimeout(timeout);
    };
  }, [platform]);

  const title =
    platform === 'android'
      ? 'Opening Heal…'
      : platform === 'ios'
        ? 'Opening Heal…'
        : 'Redirecting…';

  const body =
    platform === 'android'
      ? status === 'fallback' || status === 'ready'
        ? 'Heal for Android is coming soon.'
        : 'If the app doesn’t open automatically, we’ll show you options in a moment.'
      : platform === 'ios'
        ? 'If the app doesn’t open automatically, we’ll take you to the App Store.'
        : 'Taking you to Heal…';

  const showAndroidComingSoon = platform === 'android' && (status === 'fallback' || status === 'ready');

  return (
    <div className="share-page">
      <div className="share-card">
        <div className="share-logo">Heal</div>
        <h1 className="share-title">{title}</h1>
        <p className="share-body">{body}</p>

        {showAndroidComingSoon && (
          <div className="share-actions">
            <a className="share-button" href={DESKTOP_URL}>
              Continue on web
            </a>
          </div>
        )}

        {platform === 'ios' && (
          <div className="share-actions">
            <a className="share-button" href={IOS_APP_STORE_URL}>
              Open in App Store
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default Share;

